import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useUpdateBio, useGenerateSummary } from '@/hooks/useProfile';
import { TagInput } from './TagInput';
import type { Bio, BioUpdateRequest } from '@/types/userProfile';
import { Sparkles, FileText, Briefcase, Building2, Award, GraduationCap, TrendingUp, Factory, Target, Heart, ListChecks, Plus, X } from 'lucide-react';

const bioSchema = z.object({
  summary: z.string().max(2000).optional(),
  detailedBio: z.string().max(5000).optional(),
  profileSummary: z.string().max(2000).optional(),
  currentRole: z.string().max(100).optional(),
  currentCompany: z.string().max(100).optional(),
  yearsOfExperience: z.number().min(0).max(70).optional(),
  educationLevel: z.string().max(50).optional(),
  careerStage: z.string().optional(),
  industry: z.string().max(100).optional(),
  interests: z.array(z.string()).optional(),
  winsLookingFor: z.array(z.string()).optional(),
  professionalGoals: z.array(z.string()).optional(),
  facts: z.array(z.string()).optional(),
});

type BioFormData = z.infer<typeof bioSchema>;

/** Normalize API value to string (API may return objects e.g. { winId, winDescription, targetDate, priority }). */
function toStringItem(item: unknown): string {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (typeof item === 'object') {
    const o = item as Record<string, unknown>;
    return (o.winDescription ?? o.win_description ?? o.description ?? o.name ?? JSON.stringify(item)) as string;
  }
  return String(item);
}

/** Normalize array of strings or objects to string[]. */
function toStringArray(arr: unknown[] | undefined): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.map(toStringItem).filter(Boolean);
}

interface BioEditorProps {
  profileId: string;
  bio?: Bio;
  readOnly?: boolean;
}

export const BioEditor: React.FC<BioEditorProps> = ({ profileId, bio, readOnly = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const updateBio = useUpdateBio();
  const generateSummary = useGenerateSummary();
  const [isGenerating, setIsGenerating] = useState(false);

  // Dropdown-like UX without blocking free-text editing.
  // We use <datalist> so users can either pick a suggested value or type their own.
  const careerStageOptions = [
    'Early Career',
    'Mid Career',
    'Senior',
    'Staff',
    'Lead',
    'Manager',
    'Director',
    'VP',
    'Executive',
    'Student',
    'Other',
  ];
  const industryOptions = [
    'Software / Technology',
    'Finance',
    'Consulting',
    'Healthcare',
    'Education',
    'Marketing',
    'Retail',
    'Manufacturing',
    'Energy',
    'Public Sector',
    'Other',
  ];
  const educationOptions = [
    'High School',
    'Bachelor',
    'Master',
    'PhD',
    'MBA',
    'Other',
  ];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<BioFormData>({
    resolver: zodResolver(bioSchema),
    defaultValues: {
      summary: bio?.summary || '',
      detailedBio: bio?.detailedBio || '',
      profileSummary: bio?.profileSummary || '',
      currentRole: bio?.currentRole || '',
      currentCompany: bio?.currentCompany || '',
      yearsOfExperience: bio?.yearsOfExperience,
      educationLevel: bio?.educationLevel || '',
      careerStage: bio?.careerStage || '',
      industry: bio?.industry || '',
      interests: toStringArray(bio?.interests),
      winsLookingFor: toStringArray(bio?.winsLookingFor),
      professionalGoals: toStringArray(bio?.professionalGoals),
      facts: toStringArray(bio?.facts),
    },
  });

  const watchedInterests = watch('interests') || [];
  const watchedWins = watch('winsLookingFor') || [];
  const watchedGoals = watch('professionalGoals') || [];
  const watchedFacts = watch('facts') || [];

  const onSubmit = async (data: BioFormData) => {
    // Only send fields that BioEditor manages (text fields).
    // Skills, occupations, painPoints, and winsLookingFor are managed separately
    // and have different data shapes (objects vs strings).
    const bioUpdate: BioUpdateRequest = {
      summary: data.summary || undefined,
      detailedBio: data.detailedBio || undefined,
      profileSummary: data.profileSummary || undefined,
      currentRole: data.currentRole || undefined,
      currentCompany: data.currentCompany || undefined,
      yearsOfExperience: data.yearsOfExperience,
      educationLevel: data.educationLevel || undefined,
      careerStage: data.careerStage || undefined,
      industry: data.industry || undefined,
      interests: data.interests?.length ? data.interests : undefined,
      professionalGoals: data.professionalGoals?.length ? data.professionalGoals : undefined,
      facts: data.facts?.length ? data.facts : undefined,
    };
    await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
    setIsEditing(false);
  };

  const handleGenerateSummary = async () => {
    setIsGenerating(true);
    try {
      await generateSummary.mutateAsync(profileId);
    } finally {
      setIsGenerating(false);
    }
  };

  if (readOnly && !isEditing) {
    const hasAny = !!(bio?.summary || bio?.detailedBio || bio?.profileSummary || bio?.currentRole || bio?.currentCompany);
    return (
      <div className="space-y-6">
        <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              Bio & Summary
            </CardTitle>
            <CardDescription>Profile summaries and bio information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!hasAny && (
              <p className="text-sm text-muted-foreground py-8 text-center">No bio information recorded yet.</p>
            )}
            {bio?.summary && (
              <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Summary</p>
                <p className="text-sm whitespace-pre-wrap">{bio.summary}</p>
              </div>
            )}
            {bio?.detailedBio && (
              <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Detailed bio</p>
                <p className="text-sm whitespace-pre-wrap">{bio.detailedBio}</p>
              </div>
            )}
            {bio?.profileSummary && (
              <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Profile summary</p>
                <p className="text-sm">{bio.profileSummary}</p>
              </div>
            )}
            {(bio?.currentRole || bio?.currentCompany) && (
              <div className="flex flex-wrap gap-3">
                {bio?.currentRole && (
                  <div className="rounded-lg border bg-background/60 dark:bg-background/40 px-4 py-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Current role</p>
                    <p className="text-sm font-medium">{bio.currentRole}</p>
                  </div>
                )}
                {bio?.currentCompany && (
                  <div className="rounded-lg border bg-background/60 dark:bg-background/40 px-4 py-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</p>
                    <p className="text-sm font-medium">{bio.currentCompany}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Summaries — hero-style */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                Summaries
              </CardTitle>
              <CardDescription>Profile summaries and bio information</CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateSummary}
              disabled={isGenerating}
              className="shrink-0 border-violet-300 bg-background/60 hover:bg-violet-500/10 dark:border-violet-700"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {isGenerating ? 'Generating...' : 'Generate AI Summary'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
            <Label htmlFor="summary" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Summary (max 2000 chars)
            </Label>
            <Textarea
              id="summary"
              {...register('summary')}
              maxLength={2000}
              rows={4}
              className="mt-2 resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder="Brief overview of the professional..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              {watch('summary')?.length || 0} / 2000
            </p>
            {errors.summary && <p className="text-xs text-destructive mt-1">{errors.summary.message}</p>}
          </div>
          <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
            <Label htmlFor="detailedBio" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Detailed bio (max 5000 chars)
            </Label>
            <Textarea
              id="detailedBio"
              {...register('detailedBio')}
              maxLength={5000}
              rows={6}
              className="mt-2 resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder="Career journey, challenges, skills, aspirations..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              {watch('detailedBio')?.length || 0} / 5000
            </p>
            {errors.detailedBio && <p className="text-xs text-destructive mt-1">{errors.detailedBio.message}</p>}
          </div>
          <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-4">
            <Label htmlFor="profileSummary" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Profile summary (max 2000 chars)
            </Label>
            <Textarea
              id="profileSummary"
              {...register('profileSummary')}
              maxLength={2000}
              rows={4}
              className="mt-2 resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder="Short tagline or headline..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              {watch('profileSummary')?.length || 0} / 2000
            </p>
            {errors.profileSummary && <p className="text-xs text-destructive mt-1">{errors.profileSummary.message}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Career Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Career information
          </CardTitle>
          <CardDescription>Role, company, experience, and industry</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="currentRole" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Briefcase className="h-4 w-4" /> Current role
              </Label>
              <Input id="currentRole" {...register('currentRole')} maxLength={100} className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" placeholder="e.g. VP Engineering" />
              {errors.currentRole && <p className="text-xs text-destructive">{errors.currentRole.message}</p>}
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="currentCompany" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Building2 className="h-4 w-4" /> Company
              </Label>
              <Input id="currentCompany" {...register('currentCompany')} maxLength={100} className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" placeholder="e.g. Acme Inc" />
              {errors.currentCompany && <p className="text-xs text-destructive">{errors.currentCompany.message}</p>}
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="yearsOfExperience" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Award className="h-4 w-4" /> Years of experience
              </Label>
              <Input id="yearsOfExperience" type="number" min={0} max={70} {...register('yearsOfExperience', { valueAsNumber: true })} className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" placeholder="0–70" />
              {errors.yearsOfExperience && <p className="text-xs text-destructive">{errors.yearsOfExperience.message}</p>}
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="educationLevel" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <GraduationCap className="h-4 w-4" /> Education
              </Label>
              <Input
                id="educationLevel"
                {...register('educationLevel')}
                maxLength={50}
                list="education-options"
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                placeholder="e.g. MBA"
              />
              {errors.educationLevel && <p className="text-xs text-destructive">{errors.educationLevel.message}</p>}
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="careerStage" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <TrendingUp className="h-3.5 w-3.5" /> Career stage
              </Label>
              <Input
                id="careerStage"
                {...register('careerStage')}
                list="career-stage-options"
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                placeholder="e.g. Senior"
              />
            </div>
            <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
              <Label htmlFor="industry" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Factory className="h-4 w-4" /> Industry
              </Label>
              <Input
                id="industry"
                {...register('industry')}
                maxLength={100}
                list="industry-options"
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                placeholder="e.g. Software / Technology"
              />
              {errors.industry && <p className="text-xs text-destructive">{errors.industry.message}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Goals & Aspirations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            Goals & aspirations
          </CardTitle>
          <CardDescription>Wins they want and professional goals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border bg-muted/20 p-4">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Wins looking for</Label>
            <TagInput
              tags={watchedWins}
              onAdd={(tag) => setValue('winsLookingFor', [...watchedWins, tag], { shouldDirty: true })}
              onRemove={(tag) => setValue('winsLookingFor', watchedWins.filter((t) => t !== tag), { shouldDirty: true })}
              placeholder="Add a win..."
              className="mt-3"
            />
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Professional goals</Label>
            <TagInput
              tags={watchedGoals}
              onAdd={(tag) => setValue('professionalGoals', [...watchedGoals, tag], { shouldDirty: true })}
              onRemove={(tag) => setValue('professionalGoals', watchedGoals.filter((t) => t !== tag), { shouldDirty: true })}
              placeholder="Add a goal..."
              className="mt-3"
            />
          </div>
        </CardContent>
      </Card>

      {/* Interests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500 dark:text-rose-400" />
            Interests
          </CardTitle>
          <CardDescription>Topics and areas of interest</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border bg-muted/20 p-4">
            <TagInput
              tags={watchedInterests}
              onAdd={(tag) => setValue('interests', [...watchedInterests, tag], { shouldDirty: true })}
              onRemove={(tag) => setValue('interests', watchedInterests.filter((t) => t !== tag), { shouldDirty: true })}
              placeholder="Add an interest..."
              className="mt-0"
            />
          </div>
        </CardContent>
      </Card>

      {/* Facts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            Facts
          </CardTitle>
          <CardDescription>Additional facts or notes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {watchedFacts.map((fact, index) => (
            <div key={index} className="flex items-center gap-2 rounded-lg border bg-muted/20 px-4 py-2">
              <Input
                value={typeof fact === 'string' ? fact : toStringItem(fact)}
                onChange={(e) => {
                  const next = [...watchedFacts];
                  next[index] = e.target.value;
                  setValue('facts', next, { shouldDirty: true });
                }}
                className="flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
                placeholder="Fact"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => setValue('facts', watchedFacts.filter((_, i) => i !== index), { shouldDirty: true })}
                aria-label="Remove fact"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValue('facts', [...watchedFacts, ''], { shouldDirty: true })}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add fact
          </Button>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={() => setIsEditing(false)} className="sm:order-2">
          Cancel
        </Button>
        <Button type="submit" disabled={!isDirty || updateBio.isPending} className="sm:order-1">
          {updateBio.isPending ? 'Saving...' : 'Save bio'}
        </Button>
      </div>

      {/* Suggestion lists for editable dropdown-like UX */}
      <datalist id="career-stage-options">
        {careerStageOptions.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
      <datalist id="industry-options">
        {industryOptions.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
      <datalist id="education-options">
        {educationOptions.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </form>
  );
};







